/**
 * Carousel functionality JavaScript - Team Line Custom Theme
 * 
 * FIXES APPLIED:
 * - Desktop: Arrow buttons now scroll by visible items (not just 1)
 * - Desktop: Drag-to-scroll updates live during drag, not just on release
 * - Desktop: Mouse wheel horizontal scroll support added
 * - Mobile: Horizontal scroll with 2 cards per row
 */

(function() {
  'use strict';

  // Initialize all carousels on the page
  const initCarousels = () => {
    const carousels = document.querySelectorAll('[data-carousel]');
    
    carousels.forEach((carousel) => {
      initSingleCarousel(carousel);
    });
  };
  
  /**
   * Initialize a single carousel
   */
  function initSingleCarousel(carousel) {
    const carouselTrack = carousel.querySelector('[data-carousel-track]');
    const carouselItems = carousel.querySelectorAll('[data-carousel-item]');
    const prevButton = carousel.querySelector('[data-carousel-prev]');
    const nextButton = carousel.querySelector('[data-carousel-next]');
    const dots = carousel.querySelectorAll('[data-carousel-dot]');
    const viewport = carousel.querySelector('.carousel__viewport');
    
    if (!carouselTrack || carouselItems.length === 0) return;
    
    let currentIndex = 0;
    let isDragging = false;
    let startX = 0;
    let currentTranslate = 0;
    let prevTranslate = 0;
    let autoPlayInterval = null;
    let animationID = null;
    
    const itemsPerView = parseInt(carousel.dataset.itemsPerView) || 4;
    const autoPlay = carousel.dataset.autoPlay === 'true';
    const autoPlayDelay = parseInt(carousel.dataset.autoPlayDelay) || 5000;
    const totalItems = carouselItems.length;
    
    // Get responsive items per view based on screen width
    const getResponsiveItemsPerView = () => {
      const width = window.innerWidth;
      if (width <= 480) return 2; // Mobile: 2 cards (changed from 1)
      if (width <= 768) return Math.min(2, itemsPerView);
      if (width <= 1024) return Math.min(3, itemsPerView);
      return itemsPerView;
    };
    
    // Calculate item width and gap
    const getItemMetrics = () => {
      const firstItem = carouselItems[0];
      if (!firstItem) return { itemWidth: 0, gap: 0 };
      
      const itemStyle = getComputedStyle(firstItem);
      const trackStyle = getComputedStyle(carouselTrack);
      
      const itemWidth = firstItem.offsetWidth;
      const gap = parseInt(trackStyle.gap) || parseInt(trackStyle.columnGap) || 16;
      
      return { itemWidth, gap };
    };
    
    // Calculate max scroll index
    const getMaxIndex = () => {
      const visibleItems = getResponsiveItemsPerView();
      return Math.max(0, totalItems - visibleItems);
    };
    
    // Get translate value for index
    const getTranslateForIndex = (index) => {
      const { itemWidth, gap } = getItemMetrics();
      return index * (itemWidth + gap);
    };
    
    // Update carousel position with smooth animation
    const updateCarousel = (index, animate = true) => {
      const maxIndex = getMaxIndex();
      currentIndex = Math.max(0, Math.min(index, maxIndex));
      
      const translateX = getTranslateForIndex(currentIndex);
      prevTranslate = -translateX;
      currentTranslate = -translateX;
      
      if (animate) {
        carouselTrack.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        carouselTrack.style.transition = 'none';
      }
      
      carouselTrack.style.transform = `translateX(${-translateX}px)`;
      
      // Update buttons
      if (prevButton) {
        prevButton.disabled = currentIndex === 0;
        prevButton.setAttribute('aria-disabled', String(currentIndex === 0));
      }
      if (nextButton) {
        nextButton.disabled = currentIndex >= maxIndex;
        nextButton.setAttribute('aria-disabled', String(currentIndex >= maxIndex));
      }
      
      // Update dots
      const visibleItems = getResponsiveItemsPerView();
      const dotsCount = Math.ceil(totalItems / visibleItems);
      const activeDotIndex = Math.floor(currentIndex / visibleItems);
      
      dots.forEach((dot, i) => {
        const isActive = i === activeDotIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });
    };
    
    // Navigate by ONE item per arrow click (step scrolling, not jumping)
    const nextSlide = () => {
      const maxIndex = getMaxIndex();
      
      if (currentIndex < maxIndex) {
        // Move by 1 item at a time
        updateCarousel(currentIndex + 1);
      } else if (autoPlay) {
        updateCarousel(0); // Loop back to start
      }
    };
    
    const prevSlide = () => {
      if (currentIndex > 0) {
        // Move by 1 item at a time
        updateCarousel(currentIndex - 1);
      } else if (autoPlay) {
        updateCarousel(getMaxIndex()); // Loop to end
      }
    };
    
    // Go to specific dot
    const goToDot = (dotIndex) => {
      const visibleItems = getResponsiveItemsPerView();
      const targetIndex = dotIndex * visibleItems;
      updateCarousel(targetIndex);
    };
    
    // Auto-play functionality
    const startAutoPlay = () => {
      if (autoPlay && !autoPlayInterval) {
        autoPlayInterval = setInterval(nextSlide, autoPlayDelay);
      }
    };
    
    const stopAutoPlay = () => {
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
        autoPlayInterval = null;
      }
    };
    
    // ----- DRAG SCROLL (LIVE UPDATE) -----
    
    const setPositionByTranslate = () => {
      carouselTrack.style.transform = `translateX(${currentTranslate}px)`;
    };
    
    const animation = () => {
      setPositionByTranslate();
      if (isDragging) {
        animationID = requestAnimationFrame(animation);
      }
    };
    
    const handleDragStart = (e) => {
      // Don't start drag on interactive elements
      if (e.target.closest('button, a, input, [data-quick-add]')) return;
      
      isDragging = true;
      startX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      
      // Store current position
      const { itemWidth, gap } = getItemMetrics();
      prevTranslate = -currentIndex * (itemWidth + gap);
      currentTranslate = prevTranslate;
      
      carouselTrack.style.transition = 'none';
      carouselTrack.style.cursor = 'grabbing';
      
      stopAutoPlay();
      
      // Start animation loop for live updates
      animationID = requestAnimationFrame(animation);
      
      // Prevent default to stop text selection
      if (e.type === 'mousedown') {
        e.preventDefault();
      }
    };
    
    const handleDragMove = (e) => {
      if (!isDragging) return;
      
      const currentX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const deltaX = currentX - startX;
      
      // Calculate new translate with bounds
      const maxTranslate = 0;
      const minTranslate = -getTranslateForIndex(getMaxIndex());
      
      let newTranslate = prevTranslate + deltaX;
      
      // Add resistance at boundaries
      if (newTranslate > maxTranslate) {
        newTranslate = maxTranslate + (newTranslate - maxTranslate) * 0.3;
      } else if (newTranslate < minTranslate) {
        newTranslate = minTranslate + (newTranslate - minTranslate) * 0.3;
      }
      
      currentTranslate = newTranslate;
      
      // Prevent vertical scrolling on touch
      if (e.type.includes('touch') && Math.abs(deltaX) > 5) {
        e.preventDefault();
      }
    };
    
    const handleDragEnd = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      cancelAnimationFrame(animationID);
      
      carouselTrack.style.cursor = 'grab';
      
      // Calculate which index to snap to
      const { itemWidth, gap } = getItemMetrics();
      const movedBy = currentTranslate - prevTranslate;
      const threshold = (itemWidth + gap) / 4;
      
      let newIndex = currentIndex;
      
      if (movedBy < -threshold) {
        // Dragged left - go to next
        newIndex = Math.min(currentIndex + 1, getMaxIndex());
      } else if (movedBy > threshold) {
        // Dragged right - go to previous
        newIndex = Math.max(currentIndex - 1, 0);
      }
      
      updateCarousel(newIndex);
      startAutoPlay();
    };
    
    // ----- MOUSE WHEEL SCROLL -----
    // REMOVED: Do NOT intercept vertical scroll - let page scroll normally
    // Users can use drag or touch to scroll carousel horizontally
    
    // ----- EVENT LISTENERS -----
    
    // Arrow buttons
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        stopAutoPlay();
        prevSlide();
        startAutoPlay();
      });
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        stopAutoPlay();
        nextSlide();
        startAutoPlay();
      });
    }
    
    // Dots
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoPlay();
        goToDot(index);
        startAutoPlay();
      });
    });
    
    // Touch events (for mobile swipe)
    carouselTrack.addEventListener('touchstart', handleDragStart, { passive: true });
    carouselTrack.addEventListener('touchmove', handleDragMove, { passive: false });
    carouselTrack.addEventListener('touchend', handleDragEnd);
    carouselTrack.addEventListener('touchcancel', handleDragEnd);
    
    // Mouse events (for desktop drag)
    carouselTrack.addEventListener('mousedown', handleDragStart);
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    
    // REMOVED: Mouse wheel listener - let page scroll vertically normally
    
    // Pause autoplay on hover
    if (autoPlay) {
      carousel.addEventListener('mouseenter', stopAutoPlay);
      carousel.addEventListener('mouseleave', startAutoPlay);
    }
    
    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateCarousel(currentIndex, false);
      }, 100);
    });
    
    // Initialize
    updateCarousel(0, false);
    if (autoPlay) {
      startAutoPlay();
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }
  
  // Re-initialize on dynamic content load (Shopify theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const carousel = e.target.querySelector('[data-carousel]');
      if (carousel) {
        initSingleCarousel(carousel);
      }
      // Also init best sellers drag
      const bestSellers = e.target.querySelector('.best-sellers__track');
      if (bestSellers) {
        initDragScroll(bestSellers);
      }
    });
  }

  /**
   * Drag-to-scroll for native scroll containers
   * Features:
   * - Smooth live scrolling during drag
   * - Snaps to groups of cards when drag ends
   * - Only navigates on true click (no movement)
   * - Responsive group sizes based on viewport
   */
  function initDragScroll(scrollContainer) {
    if (!scrollContainer) return;
    
    let isMouseDown = false;
    let hasMoved = false; // Track if ANY movement occurred
    let startX = 0;
    let startScrollLeft = 0;
    let clickedLinkHref = null; // Store href, not element
    let preventNextClick = false; // Flag to prevent click after drag
    
    scrollContainer.style.cursor = 'grab';
    
    // Disable CSS scroll-snap (we'll handle snapping in JS)
    scrollContainer.style.scrollSnapType = 'none';
    
    // Prevent native image drag
    scrollContainer.querySelectorAll('img').forEach(img => {
      img.draggable = false;
    });
    
    // CRITICAL: Prevent clicks after drag - must be in capture phase
    scrollContainer.addEventListener('click', (e) => {
      if (preventNextClick) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        preventNextClick = false;
      }
    }, true); // Capture phase
    
    // Get responsive cards per group based on viewport
    const getCardsPerGroup = () => {
      const width = window.innerWidth;
      if (width <= 768) return 2;      // Mobile: 2 cards per group
      if (width <= 1024) return 3;     // Small tablet: 3 cards per group
      if (width <= 1200) return 4;     // Tablet: 4 cards per group
      return 5;                         // Desktop: 5 cards per group
    };
    
    // Get the width of one card including gap
    const getCardWidth = () => {
      const firstCard = scrollContainer.querySelector('.best-sellers__item, .product-card');
      if (!firstCard) return 200;
      const style = window.getComputedStyle(scrollContainer);
      const gap = parseFloat(style.gap) || 16;
      return firstCard.offsetWidth + gap;
    };
    
    // Snap to nearest group of cards with custom animation
    const snapToGroup = () => {
      const cardWidth = getCardWidth();
      const cardsPerGroup = getCardsPerGroup(); // Get responsive group size
      const groupWidth = cardWidth * cardsPerGroup;
      const currentScroll = scrollContainer.scrollLeft;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      
      // Calculate nearest group start position
      const groupIndex = Math.round(currentScroll / groupWidth);
      let targetScroll = groupIndex * groupWidth;
      
      // Clamp to valid range
      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));
      
      // Custom smooth scroll with slower duration (800ms)
      const startScroll = scrollContainer.scrollLeft;
      const distance = targetScroll - startScroll;
      const duration = 800; // Slower animation duration
      const startTime = performance.now();
      
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        
        scrollContainer.scrollLeft = startScroll + (distance * easedProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
    };
    
    scrollContainer.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('button, [data-quick-add]')) return;
      
      isMouseDown = true;
      hasMoved = false;
      preventNextClick = false;
      startX = e.pageX;
      startScrollLeft = scrollContainer.scrollLeft;
      
      // Store the href of clicked link (if any)
      const link = e.target.closest('a');
      clickedLinkHref = link ? link.href : null;
      
      scrollContainer.style.cursor = 'grabbing';
      scrollContainer.style.scrollBehavior = 'auto'; // Disable smooth scroll during drag
      
      e.preventDefault();
    });
    
    document.addEventListener('mousemove', (e) => {
      if (!isMouseDown) return;
      
      const deltaX = e.pageX - startX;
      
      // Mark as moved if there's ANY movement
      if (Math.abs(deltaX) > 0) {
        hasMoved = true;
      }
      
      // Live scroll during drag
      scrollContainer.scrollLeft = startScrollLeft - deltaX;
    });
    
    document.addEventListener('mouseup', (e) => {
      if (!isMouseDown) return;
      
      const wasDrag = hasMoved;
      const href = clickedLinkHref;
      
      // Reset state
      isMouseDown = false;
      clickedLinkHref = null;
      scrollContainer.style.cursor = 'grab';
      scrollContainer.style.scrollBehavior = '';
      
      if (wasDrag) {
        // User dragged - prevent any click navigation and snap to group
        preventNextClick = true;
        hasMoved = false;
        snapToGroup();
        
        // Reset prevent flag after a short delay (in case click doesn't fire)
        setTimeout(() => {
          preventNextClick = false;
        }, 100);
      } else if (href) {
        // User clicked without moving - navigate
        hasMoved = false;
        window.location.href = href;
      }
    });
    
    // Handle touch for mobile (same logic)
    let touchStartX = 0;
    let touchStartScrollLeft = 0;
    let touchHasMoved = false;
    
    scrollContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX;
      touchStartScrollLeft = scrollContainer.scrollLeft;
      touchHasMoved = false;
      scrollContainer.style.scrollBehavior = 'auto';
    }, { passive: true });
    
    scrollContainer.addEventListener('touchmove', (e) => {
      const deltaX = e.touches[0].pageX - touchStartX;
      if (Math.abs(deltaX) > 0) {
        touchHasMoved = true;
      }
      scrollContainer.scrollLeft = touchStartScrollLeft - deltaX;
    }, { passive: true });
    
    scrollContainer.addEventListener('touchend', () => {
      scrollContainer.style.scrollBehavior = '';
      if (touchHasMoved) {
        snapToGroup();
      }
    });
  }
  
  /**
   * Initialize drag scroll for all Best Sellers sections
   */
  function initBestSellersDrag() {
    const bestSellersTracks = document.querySelectorAll('.best-sellers__track');
    bestSellersTracks.forEach(track => {
      initDragScroll(track);
    });
  }
  
  // Initialize Best Sellers drag on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBestSellersDrag);
  } else {
    initBestSellersDrag();
  }

  /**
   * ============================================
   * SHOP BY ACTIVITY CAROUSEL (SwimOutlet Style)
   * ============================================
   * Features:
   * - Arrow buttons scroll by 2 cards at a time
   * - Drag-to-scroll on desktop
   * - Touch swipe on mobile
   * - Arrows disabled at boundaries
   */
  function initActivityCarousels() {
    const carousels = document.querySelectorAll('[data-activity-carousel]');
    carousels.forEach(carousel => initActivityCarousel(carousel));
  }

  function initActivityCarousel(carousel) {
    const track = carousel.querySelector('[data-activity-track]');
    const cards = carousel.querySelectorAll('[data-activity-card]');
    const prevBtn = carousel.querySelector('[data-activity-arrow="prev"]');
    const nextBtn = carousel.querySelector('[data-activity-arrow="next"]');

    if (!track || cards.length === 0) return;

    // ----- HELPERS -----

    // Get card width + gap for scroll calculations
    const getScrollUnit = () => {
      const firstCard = cards[0];
      if (!firstCard) return 220;
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.gap) || 16;
      return firstCard.offsetWidth + gap;
    };

    // Get number of cards to scroll per arrow click (2 on desktop, 1 on mobile)
    const getCardsPerScroll = () => {
      return window.innerWidth <= 600 ? 1 : 2;
    };

    // Update arrow button disabled states
    const updateArrowStates = () => {
      if (!prevBtn && !nextBtn) return;

      const scrollLeft = track.scrollLeft;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const threshold = 5; // Small threshold for float rounding

      if (prevBtn) {
        const atStart = scrollLeft <= threshold;
        prevBtn.disabled = atStart;
        prevBtn.setAttribute('aria-disabled', String(atStart));
        prevBtn.style.opacity = atStart ? '0.4' : '1';
        prevBtn.style.pointerEvents = atStart ? 'none' : 'auto';
      }

      if (nextBtn) {
        const atEnd = scrollLeft >= maxScroll - threshold;
        nextBtn.disabled = atEnd;
        nextBtn.setAttribute('aria-disabled', String(atEnd));
        nextBtn.style.opacity = atEnd ? '0.4' : '1';
        nextBtn.style.pointerEvents = atEnd ? 'none' : 'auto';
      }
    };

    // ----- ARROW CLICK BEHAVIOR -----

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const distance = getScrollUnit() * getCardsPerScroll();
        track.scrollBy({ left: -distance, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const distance = getScrollUnit() * getCardsPerScroll();
        track.scrollBy({ left: distance, behavior: 'smooth' });
      });
    }

    // Update arrow states on scroll
    track.addEventListener('scroll', updateArrowStates, { passive: true });

    // ----- DRAG SCROLL (Desktop) -----

    let isDragging = false;
    let hasMoved = false;
    let startX = 0;
    let startScrollLeft = 0;
    let clickedLinkHref = null;
    let preventNextClick = false;

    track.style.cursor = 'grab';

    // Prevent native image drag
    track.querySelectorAll('img').forEach(img => {
      img.draggable = false;
    });

    // Block click after drag (capture phase)
    track.addEventListener('click', (e) => {
      if (preventNextClick) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        preventNextClick = false;
      }
    }, true);

    track.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      if (e.target.closest('button')) return;

      isDragging = true;
      hasMoved = false;
      preventNextClick = false;
      startX = e.pageX;
      startScrollLeft = track.scrollLeft;

      // Store href if clicking a link
      const link = e.target.closest('a');
      clickedLinkHref = link ? link.href : null;

      track.style.cursor = 'grabbing';
      track.style.scrollBehavior = 'auto';

      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;

      const deltaX = e.pageX - startX;

      if (Math.abs(deltaX) > 3) {
        hasMoved = true;
      }

      // Live scroll during drag
      track.scrollLeft = startScrollLeft - deltaX;
    });

    document.addEventListener('mouseup', (e) => {
      if (!isDragging) return;

      const wasDrag = hasMoved;
      const href = clickedLinkHref;

      // Reset state
      isDragging = false;
      clickedLinkHref = null;
      track.style.cursor = 'grab';
      track.style.scrollBehavior = '';

      if (wasDrag) {
        // Dragged - prevent click (no snap, free scrolling)
        preventNextClick = true;
        hasMoved = false;

        setTimeout(() => {
          preventNextClick = false;
        }, 100);
      } else if (href) {
        // Clicked without moving - navigate
        hasMoved = false;
        window.location.href = href;
      }
    });

    // ----- TOUCH SCROLL (Mobile) -----

    let touchStartX = 0;
    let touchStartScrollLeft = 0;
    let touchHasMoved = false;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX;
      touchStartScrollLeft = track.scrollLeft;
      touchHasMoved = false;
      track.style.scrollBehavior = 'auto';
    }, { passive: true });

    track.addEventListener('touchmove', (e) => {
      const deltaX = e.touches[0].pageX - touchStartX;
      if (Math.abs(deltaX) > 5) {
        touchHasMoved = true;
      }
      track.scrollLeft = touchStartScrollLeft - deltaX;
    }, { passive: true });

    track.addEventListener('touchend', () => {
      track.style.scrollBehavior = '';
      // No snap - free scrolling continues naturally
    });

    // ----- INITIALIZE -----

    // Initial arrow state
    updateArrowStates();

    // Update on resize
    window.addEventListener('resize', () => {
      updateArrowStates();
    });
  }

  // Initialize Activity Carousels on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initActivityCarousels);
  } else {
    initActivityCarousels();
  }

  // Re-init on Shopify theme editor section load
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const activityCarousel = e.target.querySelector('[data-activity-carousel]');
      if (activityCarousel) {
        initActivityCarousel(activityCarousel);
      }
    });
  }
})();
